import { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, X, Send, User2 } from "lucide-react";
import api from "@/lib/api";

const PERSONA = "Arushi";
const STORAGE_KEY = "stx_concierge_sid";

export default function ConciergeWidget() {
    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [humanTakenOver, setHumanTakenOver] = useState(false);
    const [agentName, setAgentName] = useState(null);
    const scrollRef = useRef(null);
    const pollRef = useRef(null);

    // Start session lazily on first open
    useEffect(() => {
        if (!open || sessionId) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await api.post("/concierge/start", { entry_page: window.location.pathname, utm: getUtm() });
                if (cancelled) return;
                localStorage.setItem(STORAGE_KEY, r.data.session_id);
                setSessionId(r.data.session_id);
                setMessages([{ role: "assistant", content: r.data.greeting }]);
            } catch (e) {
                setMessages([{ role: "assistant", content: "Hi — I'm Arushi. Tap WhatsApp below if the chat fails to load." }]);
            }
        })();
        return () => { cancelled = true; };
    }, [open, sessionId]);

    // If sessionId exists in storage but no messages loaded yet — show a soft welcome on open
    useEffect(() => {
        if (open && sessionId && messages.length === 0) {
            setMessages([{ role: "assistant", content: `Welcome back! I'm ${PERSONA}. How can I help with your Styletto IX today?` }]);
        }
    }, [open, sessionId, messages.length]);

    // Poll for human agent replies while open
    useEffect(() => {
        if (!open || !sessionId) return;
        const tick = async () => {
            try {
                const r = await api.get(`/concierge/poll/${sessionId}`);
                const { messages: newMsgs, human_taken_over, agent_name } = r.data || {};
                if (human_taken_over !== undefined) setHumanTakenOver(!!human_taken_over);
                if (agent_name) setAgentName(agent_name);
                if (newMsgs && newMsgs.length) {
                    setMessages((prev) => [...prev, ...newMsgs.map((m) => ({ role: "agent", content: m.content, agent_name: agent_name }))]);
                }
            } catch (e) { /* poll error — silent */ }
        };
        pollRef.current = setInterval(tick, 4500);
        return () => clearInterval(pollRef.current);
    }, [open, sessionId]);

    // Auto-scroll
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    const send = async () => {
        const text = input.trim();
        if (!text || sending || !sessionId) return;
        setMessages((p) => [...p, { role: "user", content: text }]);
        setInput("");
        setSending(true);
        try {
            const r = await api.post("/concierge/message", { session_id: sessionId, message: text, page_url: window.location.href });
            const { messages: newMsgs, human_taken_over, agent_name } = r.data || {};
            setHumanTakenOver(!!human_taken_over);
            if (agent_name) setAgentName(agent_name);
            if (newMsgs && newMsgs.length) {
                setMessages((p) => [...p, ...newMsgs.map((m) => ({ role: m.role || "assistant", content: m.content, agent_name }))]);
            }
        } catch (e) {
            setMessages((p) => [...p, { role: "assistant", content: "I had a small hiccup — please try again, or tap WhatsApp." }]);
        } finally {
            setSending(false);
        }
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const speaker = useMemo(() => (humanTakenOver ? (agentName || "HearClear expert") : PERSONA), [humanTakenOver, agentName]);

    return (
        <>
            {/* Floating bubble */}
            {!open && (
                <button
                    data-testid="concierge-open-btn"
                    onClick={() => setOpen(true)}
                    className="fixed bottom-24 right-5 z-[60] md:right-7 md:bottom-28 rounded-full shadow-2xl px-5 py-3 text-white font-semibold flex items-center gap-2 transition-all hover:scale-105"
                    style={{ background: "#003E4D" }}
                    aria-label="Open chat with Arushi"
                >
                    <MessageCircle size={20} />
                    <span className="text-sm">Chat with {PERSONA}</span>
                </button>
            )}

            {/* Chat panel */}
            {open && (
                <div
                    data-testid="concierge-panel"
                    className="fixed z-[70] bottom-5 right-5 left-5 md:left-auto md:right-7 md:bottom-7 md:w-[380px] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border"
                    style={{ height: "min(72vh, 580px)", borderColor: "#003E4D33" }}
                >
                    {/* Header */}
                    <div className="px-4 py-3 text-white flex items-center justify-between" style={{ background: "#003E4D" }}>
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center font-bold">A</div>
                            <div>
                                <div className="font-semibold text-sm flex items-center gap-2">
                                    {speaker}
                                    {humanTakenOver && (
                                        <span className="text-[10px] font-medium bg-emerald-500/20 text-emerald-100 border border-emerald-300/30 px-1.5 py-0.5 rounded">Live agent</span>
                                    )}
                                </div>
                                <div className="text-[11px] opacity-80">Signia Styletto IX expert</div>
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/10 rounded" aria-label="Close chat" data-testid="concierge-close-btn">
                            <X size={18} />
                        </button>
                    </div>

                    {/* Messages */}
                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-stone-50">
                        {messages.map((m, i) => (
                            <Bubble key={i} role={m.role} content={m.content} />
                        ))}
                        {sending && <Bubble role="assistant" content="…" muted />}
                    </div>

                    {/* Input */}
                    <div className="p-2 border-t bg-white">
                        <div className="flex items-end gap-2">
                            <textarea
                                data-testid="concierge-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={onKey}
                                rows={1}
                                placeholder={humanTakenOver ? `Message ${speaker}…` : "Ask about Styletto IX, EMI, trial…"}
                                className="flex-1 resize-none rounded-lg border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#003E4D]"
                                style={{ minHeight: 38, maxHeight: 120 }}
                            />
                            <button
                                data-testid="concierge-send-btn"
                                onClick={send}
                                disabled={sending || !input.trim()}
                                className="rounded-lg px-3 py-2 text-white disabled:opacity-40"
                                style={{ background: "#003E4D" }}
                                aria-label="Send"
                            >
                                <Send size={16} />
                            </button>
                        </div>
                        <div className="text-[10px] text-stone-500 mt-1 px-1">
                            Powered by AI · You can ask for a human expert any time
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function Bubble({ role, content, muted }) {
    const isUser = role === "user";
    const isAgent = role === "agent";
    const align = isUser ? "justify-end" : "justify-start";
    const bg = isUser
        ? "bg-[#003E4D] text-white"
        : isAgent
            ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
            : "bg-white text-stone-800 border border-stone-200";
    return (
        <div className={`flex ${align}`}>
            <div className={`${bg} rounded-2xl px-3.5 py-2.5 text-sm max-w-[82%] shadow-sm ${muted ? "opacity-60" : ""}`}>
                {isAgent && <div className="text-[10px] font-semibold mb-0.5 flex items-center gap-1 text-emerald-700"><User2 size={11}/> Human expert</div>}
                <div className="whitespace-pre-wrap leading-relaxed">{content}</div>
            </div>
        </div>
    );
}

function getUtm() {
    try {
        const q = new URLSearchParams(window.location.search);
        const utm = {};
        ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
            const v = q.get(k);
            if (v) utm[k] = v;
        });
        return utm;
    } catch { return {}; }
}
